import React, { useMemo, useState, Fragment } from 'react'
import { Row, Col } from 'react-flexbox-grid'
import { useTranslation } from 'react-i18next'
import get from 'lodash/get'
import { useQuery, useMutation } from 'components/graphql'

import { DataTable } from 'components/DataTable'
import { Modal } from 'components/Modal'
import Button from 'components/Button'

import { ReactComponent as Edit } from 'media/icons/edit.svg'
import { ReactComponent as Trashbin } from 'media/icons/trashbin.svg'
import { ContentBlock, ExpandableBlocks } from 'components/ContentBlock'
import { Form, Text, Checkbox, Number as NumberInput, Select } from 'components/forms'
import { FormRow, FormCol } from 'style/styled'
import globalStyles from 'style/app.module.scss'

import getAdditionalConfig from '../../gql/getAppConfigAdditional'
import updateGlobalCR from '../../gql/updateGlobalCR'
import { listCategories } from 'sections/category/gql'
import { listProducts } from 'sections/product/gql/query'

import listLoyaltyCardGroups from 'sections/loyaltyCard/gql/listLoyaltyCardGroups'
import addLoyaltyCardGroup from 'sections/loyaltyCard/gql/addLoyaltyCardGroup'
import updateLoyaltyCardGroup from 'sections/loyaltyCard/gql/updateLoyaltyCardGroup'
import deleteLoyaltyCardGroup from 'sections/loyaltyCard/gql/deleteLoyaltyCardGroup'
import { StyledSection } from './styled'
import listCurrencyUnits from '../../gql/listCurrencyUnits'

const CashFlow = ({ formData, onUpdate, className, ...props }) => {
	const { section } = props
	const [t, i18n] = useTranslation()
	const { data } = useQuery(getAdditionalConfig, {
		fetchPolicy: 'cache-and-network'
	})
	const { data: groupItem } = useQuery(listLoyaltyCardGroups)
	const refetchLoyaltyCardQuery = [{ query: listLoyaltyCardGroups }]

	const [addLoyaltyCardGroupItem] = useMutation(addLoyaltyCardGroup, {
		refetchQueries: refetchLoyaltyCardQuery
	})
	const [updateLoyaltyCardGroupItem] = useMutation(updateLoyaltyCardGroup, {
		refetchQueries: refetchLoyaltyCardQuery
	})
	const [deleteLoyaltyCardGroupItem] = useMutation(deleteLoyaltyCardGroup, {
		refetchQueries: refetchLoyaltyCardQuery
	})

	const [buttonState, setButtonState] = useState(true)
	const [mutateData, setMutateData] = useState(false)
	const [originalData, setOriginalData] = useState([])
	const [inputValue, setInputValue] = useState(null)
	const [errorInput, setErrorInput] = useState(null)

	const handleRowClick = (_, { original }) => {
		setOriginalData(original)
	}

	const handleSelect = (original) => {
		setInputValue(original.name)
		setMutateData(original)
	}

	const groupListItem = get(groupItem, 'listLoyaltyCardGroups.items', [])
	const tableData = groupListItem.filter(({ name }) => name)

	const handleOnChange = (e) => {
		const { target: { value } } = e
		setInputValue(value)

		const val = value.trim()
		if(!val) {
			setButtonState(false)
			setErrorInput(t('validators.required'))
			return
		}
		const isExisting = tableData.some(({ name }) => name === val)

		if(isExisting) {
			setButtonState(false)
			setErrorInput(t('validators.duplicate'))
		} else {
			setButtonState(true)
			setErrorInput('')
		}
	}

	const columns = () => [
		{
			Header: t('loyalty_card_section.strings.group'),
			accessor: 'name'
		}
	]

	const handleSubmitGroup = () => {
		if(!mutateData?.id) {
			addLoyaltyCardGroupItem({
				variables: {
					fields: {
						name: inputValue
					}
				}
			})
		} else {
			updateLoyaltyCardGroupItem({
				variables: {
					id: mutateData?.id,
					fields: {
						name: inputValue
					}
				}
			})
		}
		setMutateData(prev => !prev)
	}

	const handleDeleteGroupItem = (id) => {
		deleteLoyaltyCardGroupItem({
			variables: { id: id }
		})
	}

	const { __typename, ...restVals } = get(data, 'getAppConfigAdditional', {})
	const initialValues = { ...formData, additionalConfig: restVals }
	const handleSubmit = ({ additionalConfig, ...vals }) => {
		const { cashRegisterGlobal = {} } = vals
		const mutateVals = {
			...vals,
			cashRegisterGlobal: {
				...cashRegisterGlobal,
				pointsProduct: cashRegisterGlobal.pointsProduct ? cashRegisterGlobal.pointsProduct.id : null,
				pointsCategory: cashRegisterGlobal.pointsCategory ? cashRegisterGlobal.pointsCategory.id : null
			}
		}
		onUpdate(mutateVals)
		const isDiscountNamesChanged = Array.isArray(additionalConfig?.discountGroupNames) &&
            additionalConfig.discountGroupNames.some((item, i) => item !== initialValues.additionalConfig.discountGroupNames[i])
		if(isDiscountNamesChanged) {
			onUpdate(additionalConfig, {
				mutation: updateGlobalCR.discountNames,
				refetchQueries: [{ query: getAdditionalConfig }]
			})
		}
		const defaultDiscountVal = additionalConfig.defaultDiscountMobileCard
		if(typeof defaultDiscountVal === 'string' && defaultDiscountVal !== initialValues.defaultDiscountMobileCard) {
			onUpdate({ defaultDiscountMobileCard: defaultDiscountVal }, {
				mutation: updateGlobalCR.discountNames,
				refetchQueries: [{ query: getAdditionalConfig }]
			})
		}
	}

	const getCurrencies = () => {
		const listCurrencies = []
		for (let i = 1; i < 4; i++) {
			listCurrencies.push(
				<StyledSection label={`${t('options_section.strings.currency')} ${i}`} key={i}>
					<Row>
						<Col xs={2}>
							<Text field={`innerNameCurrency${i}`} label={t('options_section.fields.internal_name')} />
						</Col>
						<Col xs>
							<Select
								field={`outerNameCurrency${i}`}
								isClearable
								valueField='code'
								labelField='name'
								label={t('options_section.fields.external_name')}
								remoteOptions={{
									query: listCurrencyUnits, variables: { language: i18n.language }
								}}
							/>
						</Col>
					</Row>
				</StyledSection>
			)
		}
		return listCurrencies
	}
	const expenses = Array.isArray(formData.specialProductExpenses) ?
		formData.specialProductExpenses.map((item, i, arr) => (
			<FormCol xs key={i}>
				<Text field={`specialProductExpenses[${i}]`} label={`${t('options_section.fields.inputs_outputs')} ${i + 1}`} />
				{/* Empty tag is used for bottom-margin of not last child Text */}
				{i !== (arr.length - 1) && <span />}
			</FormCol>
		))
		: null
	const getDiscounts = () => {
		const out = []
		for(let i = 0; i < 20; i++) {
			out.push(
				<Text key={i} field={`additionalConfig.discountGroupNames[${i}]`} label={`${t('options_section.fields.discount')} ${i + 1}`} inline />
			)
		}
		return out
	}
	const cardDiscountsOptions = useMemo(() => {
		const discounts = data?.getAppConfigAdditional?.discountGroupNames
		if(!Array.isArray(discounts)) {
			return []
		}
		const out = discounts.map((discountName, i) => ({ label: discountName || `${t('options_section.fields.discount')} ${i + 1}`, value: `${i + 1}` }))
		out.unshift({ label: t('common.strings.no'), value: '0' })
		return out
	}, [data?.getAppConfigAdditional?.discountGroupNames])

	return(
		<ContentBlock label={t('options_section.sub_sections.cash_flow')} className={className}>
			<Form initialValues={initialValues} onSubmit={handleSubmit} smartSubmit className={globalStyles['']}>
				{({ formState: { values }, formApi }) => {
					const checkPointsItemsDisabled = (allVals = {}) => allVals.cashRegisterGlobal && (!allVals.cashRegisterGlobal.pointsToActivate || allVals.cashRegisterGlobal.pointsToActivate === '0')
					const isPointsItemsDisabled = checkPointsItemsDisabled(values)
					const pointsItemValidation = (val, vals) => {
						if(!checkPointsItemsDisabled(vals)) {
							const { cashRegisterGlobal: { pointsProduct, pointsCategory } } = vals
							return !pointsProduct && !pointsCategory ? 'Field "Artikel" or "Category" must be defined.' : null
						}
						return null
					}
					return(
						<ExpandableBlocks nested>
							<ContentBlock label={t('options_section.strings.credit_cards')} startExpanded>
								<Row>
									<Col xs>
										<Text field={`creditCards[0]`} label={`${t('common.fields.name')} 1`} />
										<Text field={`creditCards[2]`} label={`${t('common.fields.name')} 3`} />
										<Text field={`creditCards[4]`} label={`${t('common.fields.name')} 5`} />
										<Text field={`creditCards[6]`} label={`${t('common.fields.name')} 7`} />
										<Text field={`creditCards[8]`} label={`${t('common.fields.name')} 9`} />
									</Col>
									<Col xs>
										<Text field={`creditCards[1]`} label={`${t('common.fields.name')} 2`} />
										<Text field={`creditCards[3]`} label={`${t('common.fields.name')} 4`} />
										<Text field={`creditCards[5]`} label={`${t('common.fields.name')} 6`} />
										<Text field={`creditCards[7]`} label={`${t('common.fields.name')} 8`} />
										<Text field={`creditCards[9]`} label={`${t('common.fields.name')} 10`} />
									</Col>
									<Col xs style={{
										display: 'flex',
										alignItems: 'end',
										paddingBottom: '10px'
									}}>
										<Checkbox field='cashRegisterGlobal.useForCreditCustomerPayments' label={t('options_section.fields.useForCreditCustomerPayments')}/>
									</Col>
								</Row>
							</ContentBlock>
							<ContentBlock label={t('options_section.strings.foreign_currencies')}>
								{getCurrencies()}
							</ContentBlock>
							<ContentBlock label={t('options_section.fields.inputs_outputs')}>
								<FormRow>{expenses}</FormRow>
							</ContentBlock>
							<ContentBlock label={t('options_section.strings.loyalty_cards')}>
								<div>
									<Text field='cashRegisterGlobal.pointsSystemName' label={t('common.fields.name')} inline />
									<Checkbox field='cashRegisterGlobal.isActivePointsSystem' label={t('options_section.fields.active')} inline />
								</div>
								<Row>
									<Col xs>
										<NumberInput notify={['cashRegisterGlobal.pointsCategory', 'cashRegisterGlobal.pointsProduct']} field='cashRegisterGlobal.pointsToActivate' label={t('options_section.fields.points')} withControls={false} inline shrink />
										<Select
											field='cashRegisterGlobal.pointsProduct'
											label={t('common.strings.product')}
											remoteOptions={{ query: listProducts, variables: { params: { limit: 50 } } }}
											valueField='id'
											labelField='name'
											onChange={val => {
												if(val) {
													formApi.setValues({
														...values,
														cashRegisterGlobal: { ...values.cashRegisterGlobal, pointsCategory: null }
													})
												}
											}}
											validate={pointsItemValidation}
											validateOnChange
											notify={['cashRegisterGlobal.pointsCategory']}
											simpleValue={false}
											withVirtualBody
											isSearchable
											isClearable
											disabled={isPointsItemsDisabled}
											inline />
									</Col>
									<Col xs>
										<Select
											field='cashRegisterGlobal.pointsCategory'
											label={t('common.strings.category')}
											remoteOptions={{ query: listCategories, variables: { params: { limit: 50 } } }}
											valueField='id'
											labelField='name'
											onChange={val => {
												if(val) {
													formApi.setValues({
														...values,
														cashRegisterGlobal: { ...values.cashRegisterGlobal, pointsProduct: null }
													})
												}
											}}
											validate={pointsItemValidation}
											validateOnChange
											notify={['cashRegisterGlobal.pointsProduct']}
											simpleValue={false}
											withVirtualBody
											isSearchable
											disabled={isPointsItemsDisabled}
											isClearable />
									</Col>
									<Col xs>
										<Select
											field='additionalConfig.defaultDiscountMobileCard'
											label={t('options_section.fields.default_discount_mobile_card')}
											options={cardDiscountsOptions}
										/>
									</Col>
								</Row>
								<Row>
									<Col xs={6}>
										<DataTable
											label={t('options_section.fields.loyalty_card_group')}
											key={section}
											height={300}
											onRowClick={handleRowClick}
											selectRow
											rowMenu={
												[{ func: () => handleSelect(originalData), Icon: Edit }, (handleDeleteGroupItem ? { func: handleDeleteGroupItem, Icon: Trashbin } : null)]
											}
											remoteOptions={{
												query: listLoyaltyCardGroups
											}}
											onAddRecord={() => {
												setInputValue('')
												setMutateData({})
											}}
											columns={columns}
											cssHeight={true}
											resizable={false}
										/>
									</Col>
								</Row>
								{mutateData &&
                                    <Modal
                                    	unmount={() => {
                                    		setMutateData(null)
                                    		setErrorInput('')
                                    	}}
                                    	label={t('options_section.strings.add_groupe_item')}
                                    	width={500}>
                                    	<Fragment>
                                    		<Text
                                    			noForm
                                    			value={inputValue}
                                    			onChange={handleOnChange}
                                    			label={t('common.fields.name')}
                                    			error={errorInput}
                                    		/>
                                    		<Button status={buttonState ? 'primary' : 'inactive'} onClick={handleSubmitGroup} text={t('common.buttons.apply')} type='submit'/>
                                    	</Fragment>
                                    </Modal>
								}
							</ContentBlock>
							<ContentBlock label={t('options_section.strings.discount_names')}>
								<div style={{ maxWidth: '1000px' }}>
									{getDiscounts()}
								</div>
							</ContentBlock>
						</ExpandableBlocks>
					)
				}}
			</Form>
		</ContentBlock>
	)
}

export default CashFlow
